/**
 * @fileoverview Project WGS84 lat/lon stops into a fixed SVG viewBox.
 *
 * At city scale (a few miles across) an equirectangular projection is
 * indistinguishable from Mercator — and the calendar route map only renders
 * up to three stops in the same metro, so we keep the math trivial.
 *
 * The viewBox is fixed (see `VIEW_BOX_*`) and the polyline is fitted into a
 * padded inner rect so stops never touch the edge.
 */
export const VIEW_BOX_WIDTH = 800;
export const VIEW_BOX_HEIGHT = 500;
const PADDING_RATIO = 0.15;
/**
 * Project the given stops into the fixed viewBox.
 *
 * Edge cases:
 *  - 0 stops → empty array.
 *  - 1 stop  → centered in the viewBox.
 *  - All stops sharing a single lat or lon (collinear axis) → uses the
 *    available axis spread and centers on the degenerate one so the polyline
 *    never collapses to a zero-area rendering.
 */
export function projectCoordinates(stops) {
    if (stops.length === 0)
        return [];
    const lats = stops.map((s) => s.lat);
    const lons = stops.map((s) => s.lon);
    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLon = Math.min(...lons);
    const maxLon = Math.max(...lons);
    const innerLeft = VIEW_BOX_WIDTH * PADDING_RATIO;
    const innerRight = VIEW_BOX_WIDTH * (1 - PADDING_RATIO);
    const innerTop = VIEW_BOX_HEIGHT * PADDING_RATIO;
    const innerBottom = VIEW_BOX_HEIGHT * (1 - PADDING_RATIO);
    const innerWidth = innerRight - innerLeft;
    const innerHeight = innerBottom - innerTop;
    const lonSpread = maxLon - minLon;
    const latSpread = maxLat - minLat;
    return stops.map((stop) => {
        // Lon → x: when the spread is zero (single stop or vertical line), park
        // on the horizontal center instead of dividing by zero.
        const x = lonSpread === 0
            ? innerLeft + innerWidth / 2
            : innerLeft + ((stop.lon - minLon) / lonSpread) * innerWidth;
        // Lat → y: north (greater lat) draws upward, so invert the ratio.
        const y = latSpread === 0
            ? innerTop + innerHeight / 2
            : innerTop + (1 - (stop.lat - minLat) / latSpread) * innerHeight;
        return { id: stop.id, x, y };
    });
}
